import { useSelector } from 'react-redux';
import { RootState } from '~/store/store.ts';
import { useRef } from 'react';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { resetIITLib } from '~/libs/iit/lib';
import { setDiiaSignError } from '~/store/diia/slices.ts';
import { setPersonalDataApplicationsError } from '~/store/processing-personal/slices.ts';
import { revokeValidateDocumentProcessingPersonal } from '~/store/processing-personal/thunks/validateDocumentProcessingPersonalThunks.ts';
import {
  revokeGetFinalStatusDiiaSignatureProcessingPersonal,
  revokeGetStatusDiiaSignatureProcessingPersonal,
  revokeInitializeDiiaSignatureProcessingPersonal,
} from '~/store/diia/thunks/signatureProcessingPersonalThunks.ts';
import { TDiiaInitializeResponse } from '~/store/diia/types.ts';
import { SerializedError } from '@reduxjs/toolkit';
import { SignatureError } from '~/modules/processing-personal/PersonalDataApplicationForm/SignatureError.ts';
import { setIITError } from '~/store/iit/slices.ts';
import { TIITSign } from '~/store/iit/types.ts';
import { saveSigningControllerFromIIT, startIITSigningController } from '~/store/iit/thunks.ts';
import { revokePreviewDocumentSubmit } from '~/store/processing-personal/thunks/documentPreviewPersonalThunks.ts';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { TDocumentApplication } from '~/modules/processing-personal/PersonalDataApplicationForm/types.ts';

interface IApplicationSignatureRevokeProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  onSignatureSuccess: (successMessage: string) => void;
  onSignatureError: (errorMessage: string, errorCode?: string) => void;
  onQrTimeout: () => void;
}

export const ApplicationSignatureRevoke = ({
  isModalOpen,
  handleCloseModal,
  onSignatureSuccess,
  onQrTimeout,
}: IApplicationSignatureRevokeProps) => {
  const token = useSelector((state: RootState) => state.users.token);
  const { applicationId } = useSelector((state: RootState) => state.users.user.tokenData) || {};
  const dispatch = useAppDispatch();

  const {
    deepLink,
    loading: isDiiaLoading,
    error: diiaError,
  } = useSelector((state: RootState) => state.diia) || {};

  const {
    applicationDocumentId,
    loading: isProcessingPersonalLoading,
    error: processingPersonalError,
  } = useSelector((state: RootState) => state.processingPersonal) || {};

  const { isLoading: IITLoading, error: IITError } =
    useSelector((state: RootState) => state.iit) || {};

  const timerRef = useRef<{ resetTimer: () => void; startTimer: () => void }>(null);
  const ignoreErrorRef = useRef<boolean>(false);

  const isLoading = isDiiaLoading || isProcessingPersonalLoading || IITLoading;
  const error = diiaError || processingPersonalError || IITError;

  const closeAlertModal = () => {
    diiaError && dispatch(setDiiaSignError(''));
    processingPersonalError && dispatch(setPersonalDataApplicationsError(''));
    IITError && dispatch(setIITError(''));
  };

  const documents = [
    {
      id: applicationDocumentId,
      idDictWebDocument: applicationDocumentId,
      idDoc: applicationDocumentId,
      nameDoc: 'Заява на відкликання згоди на обробку персональних даних',
    },
  ] as TDocumentApplication[];

  const closeModal = () => {
    handleCloseModal();
    diiaError && dispatch(setDiiaSignError(''));
    processingPersonalError && dispatch(setPersonalDataApplicationsError(''));
    IITError && dispatch(setIITError(''));
  };

  const validateDocument = () => {
    return dispatch(revokeValidateDocumentProcessingPersonal({ applicationId }))
      .unwrap()
      .then((response) => {
        const invalidSignatures = response.documentsWithInvalidSignatures || [];
        const rnokppMismatches = response.documentsWithRNOCPPMismatch || [];
        const generatedProtocols = response.generatedProtocols || [];
        const noKepDocuments = response.noKepDocuments || [];

        if (noKepDocuments.length > 0) {
          dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
          return;
        }

        if (invalidSignatures.length > 0 || rnokppMismatches.length > 0) {
          dispatch(setPersonalDataApplicationsError(errorMessages.NO_KEP_FILE_FOUND));
          return;
        }

        if (generatedProtocols.length > 0) {
          handleSignatureSuccess(infoMessages.DOCUMENTS_SENT);
          return;
        }
      })
      .catch(() => {
        dispatch(setPersonalDataApplicationsError(errorMessages.SERVICE_UNAVAILABLE));
      });
  };

  const handleSignatureDiia = async () => {
    if (isLoading) return;
    try {
      const { payload } = await dispatch(
        revokeInitializeDiiaSignatureProcessingPersonal({
          applicationId,
          token,
        })
      );
      const requestId = (payload as TDiiaInitializeResponse)?.requestId;
      timerRef.current?.startTimer();

      const promise = dispatch(
        revokeGetStatusDiiaSignatureProcessingPersonal({
          token,
          requestId,
          applicationId,
        })
      );

      const response = await promise;
      if ((response as { error: SerializedError }).error) return;

      if ((response.payload as { status: string })?.status === 'notUsed') {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }
      if (isLoading) return;
      if ((response.payload as { status: string })?.status === 'processing') {
        const { payload: finalStatus } = await dispatch(
          revokeGetFinalStatusDiiaSignatureProcessingPersonal({
            token,
            requestId,
            applicationId,
          })
        );

        if ((finalStatus as { status: string }).status === 'done') {
          await validateDocument();
        }
      } else {
        await validateDocument();
      }
    } catch (error) {
      SignatureError(error);
    }
  };

  const handleSignatureIIT = async () => {
    if (isLoading) return;

    dispatch(
      startIITSigningController({
        path: `/personal-data/public/personal-data/revoke/applications/${applicationId}/get-hashes-to-sign`,
      })
    );
  };

  const onIITSign = async (signFiles: TIITSign[]) => {
    try {
      const res = await dispatch(
        saveSigningControllerFromIIT({
          fileList: signFiles,
          path: `/personal-data/public/personal-data/revoke/applications/${applicationId}/save-signed-documents`,
        })
      );

      if (res) {
        await validateDocument();
      } else {
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    } catch (error) {
      dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
    } finally {
      resetIITLib();
    }
  };

  const handleSignatureSuccess = (successMessage: string): void => {
    onSignatureSuccess(successMessage);
  };

  const handleQrTimeout = (): void => {
    ignoreErrorRef.current = true;
    onQrTimeout();
  };

  const onDiiaClick = (): void => {
    ignoreErrorRef.current = false;
    handleSignatureDiia();
  };

  const handlePreview = async () => {
    await dispatch(
      revokePreviewDocumentSubmit({
        applicationId,
        idDoc: applicationDocumentId,
      })
    );
  };

  return (
    <>
      <ElectronicSignature
        isModalOpen={isModalOpen}
        handleCloseModal={closeModal}
        handleSignatureDiia={onDiiaClick}
        handleSignatureIIT={handleSignatureIIT}
        onIITSign={onIITSign}
        handleTimeUp={handleQrTimeout}
        handlePreview={handlePreview}
        deepLink={deepLink}
        documents={documents}
        ref={timerRef}
        isDiiaSignatureDisabled={false}
        isIITSignatureDisabled={false}
      />

      {isLoading && <FullPageLoader />}
      {!!error && <ModalAlert isOpen={!!error} text={error} onClose={closeAlertModal} />}
    </>
  );
};
